import { useContext } from 'react'
import { RosterSetupContext } from '../RosterSetup'
import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import capitalize from 'lodash/capitalize'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const CountByRole = ({ className }: { className?: string }) => {
  const { team } = useTeam()
  const { mainsByRole, altsByRole, reservesByRole, offSpecsByRole } =
    useContext(RosterSetupContext)
  const hasReserveRanks = team.teamRanks?.some(
    (rank) => rank.player_type === 'reserve',
  )

  const mainCount = Object.values(mainsByRole).reduce(
    (acc, role) =>
      acc +
      (role?.filter((member) => member.teamRank.player_type === 'main')
        .length ?? 0),
    0,
  )
  const reserveCount = Object.values(reservesByRole).reduce(
    (acc, role) => acc + (role?.length ?? 0),
    0,
  )
  const altCount = Object.values(altsByRole).reduce(
    (acc, role) => acc + (role?.length ?? 0),
    0,
  )
  const offCount = Object.values(offSpecsByRole).reduce(
    (acc, role) => acc + (role?.length ?? 0),
    0,
  )

  return (
    <Card className={cn('w-full grow', className)} size='sm'>
      <table className='w-full'>
        <thead>
          <tr className='[&>th]:px-2 [&>th:first-child]:pl-0 [&>th]:font-normal [&>th]:text-xs'>
            <th className='text-left'>Role</th>
            <th>Main</th>
            {hasReserveRanks ? (
              <th>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>Res</span>
                  </TooltipTrigger>
                  <TooltipContent>Reserve</TooltipContent>
                </Tooltip>
              </th>
            ) : null}
            <th>Alt</th>
            <th>Off</th>
            <th>#</th>
          </tr>
        </thead>
        <tbody>
          {characterRoles.map((role) => {
            const roleMainCount =
              mainsByRole[role]?.filter(
                (member) => member.teamRank.player_type === 'main',
              ).length ?? 0
            const roleReserveCount = reservesByRole[role]?.length ?? 0
            const roleAltCount = altsByRole[role]?.length ?? 0
            const roleOffCount = offSpecsByRole[role]?.length ?? 0
            const totalCount =
              roleMainCount + roleReserveCount + roleAltCount + roleOffCount

            return (
              <tr key={role} className='border-t'>
                <td>
                  <div
                    className={`flex items-center gap-1.5 mr-2 ${
                      roleMainCount > 0 ? '' : 'opacity-60 italic'
                    }`}
                  >
                    <CharacterRoleIcon role={role} size='small' />{' '}
                    {capitalize(role)}
                  </div>
                </td>
                <td className='text-center'>
                  {roleMainCount || <span className='opacity-30'>-</span>}
                </td>
                {hasReserveRanks ? (
                  <td className='text-center'>
                    {roleReserveCount || <span className='opacity-30'>-</span>}
                  </td>
                ) : null}
                <td className='text-center'>
                  {roleAltCount || <span className='opacity-30'>-</span>}
                </td>
                <td className='text-center'>
                  {roleOffCount || <span className='opacity-30'>-</span>}
                </td>
                <td className='text-center font-semibold'>
                  {totalCount || <span className='opacity-30'>-</span>}
                </td>
              </tr>
            )
          })}

          <tr className='border-t font-semibold'>
            <td></td>
            <td className='text-center'>
              {mainCount || <span className='opacity-30'>-</span>}
            </td>
            {hasReserveRanks ? (
              <td className='text-center'>
                {reserveCount || <span className='opacity-30'>-</span>}
              </td>
            ) : null}
            <td className='text-center'>
              {altCount || <span className='opacity-30'>-</span>}
            </td>
            <td className='text-center'>
              {offCount || <span className='opacity-30'>-</span>}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </Card>
  )
}

export default CountByRole
